import * as google_maps_api from "./google_maps_api";
import { Geo } from "./messages";
import { getLocalStorageItem, setLocalStorageItem } from "./storage_utils";
import { delay } from "./system/delay";

/**
 * null means there is no result
 */
export type DistanceResult = number | null;
export type DurationResult = number | null;

export interface DistanceRequest {
  origin: Geo | string;
  destination: Geo | string;
}

interface DistanceCachedResult {
  distance: DistanceResult;

  // milliseconds timestamp (Date.getTime)
  timestamp: number;
}

export class GeoService {
  public constructor() {
    this.asyncConstructor();
    const MAX_CONCURRENCY = 8;
    this.jobQueue = new JobQueue<DistanceRequest>(MAX_CONCURRENCY, async (job) => {
      const { distance } = await getDistance(job.origin, job.destination);
      const key = cacheKey(job.origin, job.destination);
      this.distanceCache.set(key, {
        distance: distance,
        timestamp: currentTimestamp(),
      });
      this.queueDistanceCacheSave();
      const callbacks = this.distanceCallbacks.get(key);

      if (callbacks === undefined) {
        throw new Error("The Impossible Happened");
      }

      this.distanceCallbacks.delete(key);

      for (const callback of callbacks) {
        // Run in a setTimeout in case callback throws an exception
        setTimeout(() => callback(distance), 0);
      }
    });
  }

  async asyncConstructor() {
    this.distanceCache = await loadDistanceCache();
  }

  private distanceCacheSaveTimeoutHandle: NodeJS.Timer | null = null;

  queueDistanceCacheSave() {
    if (this.distanceCacheSaveTimeoutHandle !== null) {
      clearTimeout(this.distanceCacheSaveTimeoutHandle);
    }

    this.distanceCacheSaveTimeoutHandle = setTimeout(async () => {
      this.distanceCacheSaveTimeoutHandle = null;
      await saveDistanceCache(this.distanceCache);
    }, 100);
  }

  distanceImpl(req: DistanceRequest, cb: (distance: DistanceResult) => void): void {
    const key = cacheKey(req.origin, req.destination);
    const cacheLookup = this.distanceCache.get(key);
    if (cacheLookup !== undefined) {
      this.distanceCache.set(key, {
        distance: cacheLookup.distance,
        timestamp: currentTimestamp(),
      });
      this.queueDistanceCacheSave();
      cb(cacheLookup.distance);
      return;
    }

    const existingReq = this.distanceCallbacks.get(key);
    if (existingReq !== undefined) {
      existingReq.push(cb);
    } else {
      this.distanceCallbacks.set(key, [cb]);
      this.jobQueue.enqueue({
        origin: req.origin,
        destination: req.destination,
      });
    }
  }

  public async distance(req: DistanceRequest): Promise<DistanceResult> {
    return new Promise<DistanceResult>((resolve, _reject) => {
      this.distanceImpl(req, (distance) => {
        resolve(distance);
      });
    });
  }

  // TODO !!! This is broken, distanceCache can be used before it is initialized
  private distanceCache!: Map<string, DistanceCachedResult>;

  private distanceCallbacks = new Map<string, ((distance: DistanceResult) => void)[]>();

  private jobQueue: JobQueue<DistanceRequest>;
}

function currentTimestamp(): number {
  return new Date().getTime();
}

function hashPoint(p: Geo | string): string {
  if (typeof p === "string") {
    return "S" + p;
  } else {
    return "G" + p.lat + "|" + p.lng;
  }
}

function cacheKey(origin: Geo | string, destination: Geo | string): string {
  return hashPoint(origin) + hashPoint(destination);
}

const DISTANCE_CACHE_LOCALSTORAGE_KEY = "medflyt_geoservice_distance_cache";

/**
 * Saves the cache object to persistent localStorage
 */
async function saveDistanceCache(distanceCache: Map<string, DistanceCachedResult>) {
  const cutoff = currentTimestamp() - 1000 * 60 * 60 * 24 * 2;

  const vals: [string, DistanceCachedResult][] = [];
  distanceCache.forEach((value, key) => {
    if (value.timestamp > cutoff) {
      vals.push([key, value]);
    }
  });

  await setLocalStorageItem(DISTANCE_CACHE_LOCALSTORAGE_KEY, JSON.stringify(vals));
}

/**
 * Loads a previously saved cache object from persistent localStorage
 *
 * @return Empty cache if there was nothing saved
 */
async function loadDistanceCache(): Promise<Map<string, DistanceCachedResult>> {
  const json = await getLocalStorageItem(DISTANCE_CACHE_LOCALSTORAGE_KEY);
  if (json === null) {
    return new Map<string, DistanceCachedResult>();
  }

  const vals: [string, DistanceCachedResult][] = JSON.parse(json);
  const result = new Map<string, DistanceCachedResult>();
  for (const val of vals) {
    const [k, v] = val;
    result.set(k, v);
  }
  return result;
}

export async function getDistance(
  origin: Geo | string,
  destination: Geo | string,
  backoff: number = 2000,
  travelMode: google.maps.TravelMode = google.maps.TravelMode.DRIVING
): Promise<{ distance: DistanceResult; duration: DurationResult }> {
  try {
    const result = await googleMapsGetDistance(origin, destination, travelMode);
    return result;
  } catch (e) {
    await delay(backoff);
    return getDistance(origin, destination, backoff + 100);
  }
}

function googleMapsGetDistance(
  origin: Geo | string,
  destination: Geo | string,
  travelMode: google.maps.TravelMode = google.maps.TravelMode.DRIVING
): Promise<{ distance: DistanceResult; duration: DurationResult }> {
  return new Promise<{ distance: DistanceResult; duration: DurationResult }>((resolve, reject) => {
    google_maps_api.onload(() => {
      const directionsService = new google.maps.DirectionsService();

      console.log("GeoService calling DirectionsService", origin, destination);
      directionsService.route(
        {
          origin: origin,
          destination: destination,
          travelMode: travelMode,
        },
        (result, status) => {
          if (status === google.maps.DirectionsStatus.OK) {
            console.log(`DirectionsService completed`);
            const distance = result.routes[0].legs[0]?.distance?.value ?? null;
            const duration = result.routes[0].legs[0]?.duration?.value ?? null;
            resolve({ distance, duration });
          } else {
            console.log(`DirectionsService returned status ${status}. Retrying...`);
            reject(new Error());
          }
        }
      );
    });
  });
}

class JobQueue<T> {
  constructor(
    private MAX_CONCURRENCY: number,

    /**
     * Must not throw an exception
     */
    private process: (job: T) => Promise<void>
  ) {}

  private numSending = 0;
  private sendJobQueue: T[] = [];

  public enqueue(job: T): void {
    this.sendJobQueue.push(job);

    if (this.numSending < this.MAX_CONCURRENCY) {
      this.numSending++;
      this.fireNextJob();
    }
  }

  private fireNextJob(): void {
    const job = this.sendJobQueue.shift();
    if (job === undefined) {
      throw new Error("The Impossible Happened");
    }

    this.processJob(job);
  }

  private processJob(job: T): void {
    // tslint:disable-next-line:no-floating-promises
    this.process(job).then(() => {
      if (this.sendJobQueue.length > 0) {
        this.fireNextJob();
      } else {
        this.numSending--;
      }
    });
  }
}

export const geoService = new GeoService();
